import { useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { Id } from '../../../../convex/_generated/dataModel';

interface BadgesSectionProps {
  childId: Id<'players'>;
}

export function BadgesSection({ childId }: BadgesSectionProps) {
  const badges = useQuery(api.parent.badges.listForChild, { childId });

  if (badges === undefined) {
    return (
      <div className="space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Badges</p>
        <div className="grid grid-cols-3 gap-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-xl" />
          ))}
        </div>
      </div>
    );
  }

  const earned = badges?.filter((b) => b.earned) ?? [];

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Badges · {earned.length}/{badges?.length ?? 0}
      </p>
      <div className="grid grid-cols-1 gap-3">
        {badges?.map((badge) => (
          <div
            key={badge.type}
            className={cn(
              'flex flex-row items-center gap-4 p-4 rounded-xl',
              badge.earned
                ? 'bg-card shadow-sm border border-border'
                : 'opacity-30'
            )}
          >
            <img
              src={`/badges/${badge.imageAsset}`}
              alt={badge.name}
              className={cn(
                'w-20 h-20 object-contain shrink-0',
                !badge.earned && 'grayscale'
              )}
            />
            <div className="flex flex-col gap-0.5">
              <p className="text-sm font-semibold text-foreground">{badge.name}</p>
              {badge.earned && badge.awardedAt ? (
                <p className="text-xs text-muted-foreground">
                  {new Date(badge.awardedAt).toLocaleDateString('en-AU', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground italic">Not yet earned</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
